import { useEffect, useMemo, useState } from "react";
import api from "api/axiosInstance";
import styles from "../AdminPanel.module.scss";
import {
  DEFAULT_PRIVACY_POLICY_BODY,
  DEFAULT_PRIVACY_POLICY_TITLE,
  parsePrivacyPolicy,
  PRIVACY_POLICY_BODY_KEY,
  PRIVACY_POLICY_TITLE_KEY,
} from "../../../../app/privacyPolicy";

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

function extractErr(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    typeof data === "string"
      ? data
      : data?.message
      ? Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message)
      : data?.error
      ? String(data.error)
      : e?.message;

  return status ? `${msg} (HTTP ${status})` : msg || "Неизвестная ошибка";
}

export default function PrivacyPolicySettings() {
  const [rows, setRows] = useState<ClinicInfoRow[]>([]);
  const [title, setTitle] = useState(DEFAULT_PRIVACY_POLICY_TITLE);
  const [body, setBody] = useState(DEFAULT_PRIVACY_POLICY_BODY);

  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const rowsByKey = useMemo(() => {
    const map = new Map<string, ClinicInfoRow>();
    rows.forEach((row) => map.set(row.name, row));
    return map;
  }, [rows]);

  const previewSections = useMemo(() => parsePrivacyPolicy(body), [body]);

  const loadData = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      const res = await api.get<ClinicInfoRow[]>("/clinic-info");
      const loaded = res.data ?? [];
      setRows(loaded);

      const titleRow = loaded.find((x) => x.name === PRIVACY_POLICY_TITLE_KEY);
      const bodyRow = loaded.find((x) => x.name === PRIVACY_POLICY_BODY_KEY);

      setTitle((titleRow?.detail || "").trim() || DEFAULT_PRIVACY_POLICY_TITLE);
      setBody((bodyRow?.detail || "").trim() || DEFAULT_PRIVACY_POLICY_BODY);
    } catch (e: any) {
      setErrorText(`Ошибка загрузки: ${extractErr(e)}`);
      setRows([]);
      setTitle(DEFAULT_PRIVACY_POLICY_TITLE);
      setBody(DEFAULT_PRIVACY_POLICY_BODY);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const upsertClinicInfo = async (name: string, detail: string) => {
    const existing = rowsByKey.get(name);
    if (existing) {
      await api.delete(`/clinic-info/${existing.id}`);
    }
    await api.post("/clinic-info", { name, detail });
  };

  const savePolicy = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      const cleanTitle = (title || "").trim() || DEFAULT_PRIVACY_POLICY_TITLE;
      const cleanBody = (body || "").trim() || DEFAULT_PRIVACY_POLICY_BODY;

      await upsertClinicInfo(PRIVACY_POLICY_TITLE_KEY, cleanTitle);
      await upsertClinicInfo(PRIVACY_POLICY_BODY_KEY, cleanBody);

      await loadData();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const resetToDefault = () => {
    setTitle(DEFAULT_PRIVACY_POLICY_TITLE);
    setBody(DEFAULT_PRIVACY_POLICY_BODY);
  };

  return (
    <>
      <div className={styles.form_block}>
        <h3>Политика конфиденциальности</h3>

        {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

        <label>Заголовок страницы</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Введите заголовок"
        />

        <label>Текст политики</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={22}
          style={{ width: "80%", minHeight: 420, whiteSpace: "pre-wrap" }}
          placeholder={`1. Общие положения
1.1. Первый подпункт
1.2. Второй подпункт

2. Следующий раздел
2.1. Подпункт`}
        />

        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.75, maxWidth: 980 }}>
          Формат простой: раздел начинается строкой вида <b>1. Заголовок раздела</b>, а подпункты —
          строками вида <b>1.1. Текст подпункта</b>. Пустая строка между разделами допустима.
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className={styles.btn_primary} onClick={savePolicy} disabled={loading}>
            {loading ? "Сохраняю..." : "Сохранить"}
          </button>

          <button className={styles.btn} type="button" onClick={resetToDefault} disabled={loading}>
            Сбросить к шаблону
          </button>
        </div>
      </div>

      <div className={styles.form_block}>
        <h3>Предпросмотр</h3>

        <div style={{ maxWidth: 980 }}>
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 18 }}>{title}</div>

          <div style={{ display: "grid", gap: 16 }}>
            {previewSections.map((section) => (
              <div
                key={`${section.number}_${section.title}`}
                style={{
                  background: "#fff",
                  border: "1px solid #e3e3e3",
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 10 }}>
                  {section.number}. {section.title}
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  {section.items.map((item, idx) => (
                    <div key={`${item.number}_${idx}`} style={{ lineHeight: 1.6 }}>
                      {item.number ? (
                        <>
                          <span style={{ fontWeight: 700 }}>{item.number}.</span>{" "}
                          <span>{item.text}</span>
                        </>
                      ) : (
                        <span>{item.text}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}